/**
 * The two pieces of state and the one timer behind `model/departures`.
 *
 * The reconciliation runs DURING RENDER, adjusted off a signature of the current keys — React's own
 * documented way of reacting to changed input, and the only one available here. An effect would run
 * a frame after the removed row had already been painted out of the list, so the ghost would appear
 * one frame after the gap it exists to hide. A ref written in render is banned by the compiler and
 * would be the same frame late anyway.
 *
 * The timer only CLEARS. It never delays a mutation — see `model/departures` for why that
 * distinction is the whole design — so an interrupted or ignored timer costs a ghost that lingers
 * until the next list change, never a plan that disagrees with itself.
 */

import { useEffect, useState } from 'react';

import {
  mergeDepartures,
  reconcileDepartures,
  type Departure,
  type RenderSlot,
} from '../model/departures';

interface Keyed {
  readonly key: string;
}

/** A key signature, so "the list changed" is not "a new array was mapped". */
function signatureOf(items: readonly Keyed[]): string {
  return items.map((item) => item.key).join(' ');
}

/**
 * @param items The list as the plan holds it now.
 * @param durationMs How long the exit keyframe runs — the ghost is dropped after it.
 * @param subject What the list is a list OF — the day the panel is showing. When it changes, the
 *   list was EXCHANGED rather than emptied, and nothing departs; `model/departures` carries the
 *   measurement that made this a parameter rather than an assumption.
 * @returns The render list: every live item, and every ghost back in the place it left.
 */
export function useDepartures<T extends Keyed>(
  items: readonly T[],
  durationMs: number,
  subject: string,
): readonly RenderSlot<T>[] {
  const [seen, setSeen] = useState<{ signature: string; subject: string; items: readonly T[] }>({
    signature: signatureOf(items),
    subject,
    items,
  });
  const [departures, setDepartures] = useState<readonly Departure<T>[]>([]);

  const signature = signatureOf(items);
  if (seen.signature !== signature || seen.subject !== subject) {
    setDepartures(reconcileDepartures(seen.items, items, departures, seen.subject !== subject));
    setSeen({ signature, subject, items });
  }

  useEffect(() => {
    if (departures.length === 0) return undefined;
    const leaving = new Set(departures.map((departure) => departure.key));
    const timer = setTimeout(() => {
      setDepartures((current) => {
        const next = current.filter((departure) => !leaving.has(departure.key));
        // Identity is the loop guard: returning a fresh array that holds the same departures would
        // re-run this effect, start another full timer, and never settle.
        return next.length === current.length ? current : next;
      });
    }, durationMs);
    return () => {
      clearTimeout(timer);
    };
  }, [departures, durationMs]);

  return mergeDepartures(items, departures);
}
